import { z } from 'zod';

// RFC 7946 FeatureCollection. Loose objects, because GeoJSON allows foreign members.

type Position = Array<number>;

const isPosition = (p: unknown): p is Position =>
  Array.isArray(p) && p.length >= 2 && p.every((n) => typeof n === 'number');
// Plain loops instead of nested z.array: a track holds 100k+ positions.
const positions = (min: number) =>
  z.custom<Array<Position>>((ps) => Array.isArray(ps) && ps.length >= min && ps.every(isPosition));

const PositionSchema = z.custom<Position>(isPosition);
const BBoxSchema = z.array(z.number()).min(4).optional();
const LineStringCoordinatesSchema = positions(2);
const PolygonCoordinatesSchema = z.array(positions(4));

const geometry = <T extends string, C extends z.ZodType>(type: T, coordinates: C) =>
  z.looseObject({ type: z.literal(type), coordinates, bbox: BBoxSchema });

const SimpleGeometrySchema = z.discriminatedUnion('type', [
  geometry('Point', PositionSchema),
  geometry('LineString', LineStringCoordinatesSchema),
  geometry('Polygon', PolygonCoordinatesSchema),
  geometry('MultiPoint', positions(0)),
  geometry('MultiLineString', z.array(LineStringCoordinatesSchema)),
  geometry('MultiPolygon', z.array(PolygonCoordinatesSchema)),
]);

const GeometrySchema = z.union([
  SimpleGeometrySchema,
  z.looseObject({
    type: z.literal('GeometryCollection'),
    geometries: z.array(SimpleGeometrySchema),
    bbox: BBoxSchema,
  }),
]);

const FeatureSchema = z.looseObject({
  type: z.literal('Feature'),
  id: z.union([z.number(), z.string()]).optional(),
  properties: z.record(z.string(), z.unknown()).nullable(),
  geometry: GeometrySchema.nullable(),
  bbox: BBoxSchema,
});

export const FeatureCollectionSchema = z.looseObject({
  type: z.literal('FeatureCollection'),
  features: z.array(FeatureSchema),
  bbox: BBoxSchema,
});
